import { getMeterInfo } from "@features/cx/disconnect/api";
import {
  useMeterInfo,
  useWorkOrderDetails,
} from "@features/cx/disconnect/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useQueries } from "@tanstack/react-query";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

interface MeterRemovalFormProps {
  utilityServiceList?: any;
  onSubmit?: (data: any) => void;
  loading?: boolean;
  consumerData?: any;
}

export interface MeterRemovalFormRef {
  getFormValues: () => any;
  validateForm: () => Promise<boolean>;
  resetForm: () => void;
  isFormValid: () => Promise<boolean>;
}

const MeterRemovalFormWithService = forwardRef<
  MeterRemovalFormRef,
  MeterRemovalFormProps
>(
  (
    { utilityServiceList = [], onSubmit, loading = false, consumerData },
    ref,
  ) => {
    const [availableUtilityServices, setAvailableUtilityServices] = useState<
      Array<{ utility_service: string }>
    >([]);
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedWorkOrder, setSelectedWorkOrder] = useState<any>(null);

    // Dynamic state for device number searches per utility service
    const [deviceSearchTerms, setDeviceSearchTerms] = useState<
      Record<string, string>
    >({});
    const [selectedDeviceNumbers, setSelectedDeviceNumbers] = useState<
      Record<string, any>
    >({});

    const form = FormService.createForm({
      fields: formFields,
      mode: "onChange",
      validateOnMount: false,
    });

    const meterInfoQueries = useQueries({
      queries: availableUtilityServices.map((service) => {
        const premise = consumerData?.territoryDataCodes?.service?.premise;
        const serviceName = service.utility_service;
        const serviceKey = serviceName.toLowerCase();
        const searchTerm = deviceSearchTerms[serviceKey] || "";

        return {
          queryKey: ["meterInfo", searchTerm, serviceName, premise],
          queryFn: () => getMeterInfo(searchTerm, serviceName, premise),
          enabled: !!serviceName,
        };
      }),
    });

    // Get work order details from API
    const { data: workorderDetail, isLoading: isSearching } =
      useWorkOrderDetails(searchTerm);

    // Convert work order details to dropdown options
    const workOrderOptions = useMemo(() => {
      if (!workorderDetail || !Array.isArray(workorderDetail)) {
        return [];
      }
      console.log("workorderDetail", workorderDetail);
      return workorderDetail.map((order) => ({
        value: order.id,
        label: `${order.jobCardNo}`,
      }));
    }, [workorderDetail]);

    // Meter info hooks for each utility service (called at top level)
    const waterMeterInfo = useMeterInfo(deviceSearchTerms.water || "", "Water");
    const electricityMeterInfo = useMeterInfo(
      deviceSearchTerms.electricity || "",
      "Electricity",
    );
    const gasMeterInfo = useMeterInfo(deviceSearchTerms.gas || "", "Gas");

    // Create hooks object for easy access
    const meterInfoHooks = {
      water: waterMeterInfo,
      electricity: electricityMeterInfo,
      gas: gasMeterInfo,
    };

    // Create device number options for each utility service
    const getDeviceNumberOptions = useMemo(() => {
      return (
        serviceName: string,
      ): Array<{
        value: string;
        label: string;
        disabled: boolean;
        deviceData: any;
      }> => {
        const serviceIndex = availableUtilityServices.findIndex(
          (service) => service.utility_service === serviceName,
        );

        if (serviceIndex === -1) return [];

        const meterQuery = meterInfoQueries[serviceIndex];

        if (!meterQuery?.data || !Array.isArray(meterQuery.data)) {
          return [];
        }

        return meterQuery.data.map((device: any) => ({
          value: device.id,
          label: `${device.deviceNo}`,
          disabled: false,
          deviceData: device,
        }));
      };
    }, [availableUtilityServices, meterInfoQueries]);

    useImperativeHandle(ref, () => ({
      getFormValues: () => ({
        ...form.getValues(),
        selected_work_order: selectedWorkOrder,
        selected_device_numbers: selectedDeviceNumbers,
      }),
      validateForm: async () => {
        const isValid = await FormService.validateForm(form);
        return isValid;
      },
      resetForm: () => {
        handleReset();
      },
      isFormValid: async () => {
        const isValid = await FormService.validateForm(form);
        return isValid;
      },
    }));

    useEffect(() => {
      if (utilityServiceList?.length) {
        const seen = new Set();
        const uniqueServices = utilityServiceList
          .filter((service) => {
            if (seen.has(service)) return false;
            seen.add(service);
            return true;
          })
          .map((service) => ({
            utility_service: service,
          }));

        setAvailableUtilityServices(uniqueServices);
      }
    }, [utilityServiceList]);

    // Handle search term change for work orders
    const handleSearchChange = (value: string) => {
      setSearchTerm(value);
    };

    // Handle device number search for specific utility service
    const handleDeviceSearchChange = (value: string, serviceName: string) => {
      const serviceKey = serviceName.toLowerCase();
      setDeviceSearchTerms((prev) => ({
        ...prev,
        [serviceKey]: value,
      }));
    };

    // Handle device number selection for specific utility service
    const handleDeviceNumberSelection = useMemo(() => {
      return (deviceId: string, serviceName: string) => {
        const serviceKey = serviceName.toLowerCase();
        const serviceIndex = availableUtilityServices.findIndex(
          (service) => service.utility_service === serviceName,
        );

        if (serviceIndex === -1) return;

        const meterQuery = meterInfoQueries[serviceIndex];

        if (meterQuery?.data && Array.isArray(meterQuery.data)) {
          const selectedDevice = meterQuery.data.find(
            (device: any) => device.id == deviceId,
          );
          console.log("selectedDevice", selectedDevice);
          if (selectedDevice) {
            setSelectedDeviceNumbers((prev) => ({
              ...prev,
              [serviceKey]: selectedDevice,
            }));

            const fieldPrefix = serviceKey.replace(/\s+/g, "_");
            form.setValue(
              `${fieldPrefix}_meter_no`,
              selectedDevice.meterNumber || "",
            );
            form.setValue(`${fieldPrefix}_meter_id`, selectedDevice.id || "");
          }
        }
      };
    }, [availableUtilityServices, meterInfoQueries, form]);

    // 🔧 FIX: stable fingerprint that changes whenever any meter query receives fresh data.
    // This forces generateFormFields to re-run once the API resolves,
    // so the dropdown shows data on the FIRST click (not only after typing).
    const meterQueriesFingerprint = meterInfoQueries
      .map((q) => q.dataUpdatedAt)
      .join("-");

    // Generate form fields configuration
    const generateFormFields = useMemo((): FormField[] => {
      if (availableUtilityServices.length === 0) return [];

      const fields: FormField[] = [];

      console.log("availableUtilityServices", availableUtilityServices);

      availableUtilityServices.forEach((service) => {
        const serviceName = service.utility_service;
        const fieldNamePrefix = serviceName.toLowerCase().replace(/\s+/g, "_");
        const serviceKey = serviceName.toLowerCase();

        const deviceOptions = getDeviceNumberOptions(serviceName);

        // Device Number Field (Multi-select with search)
        fields.push({
          name: `${fieldNamePrefix}_device_no`,
          label: `${serviceName} Device No`,
          type: "multi-select",
          placeholder: `Search for ${serviceName.toLowerCase()} device numbers...`,
          options: deviceOptions,
          required: false,
          group: `${serviceName} Meter`,
          groupOrder: 1,
          searchable: true,
          clearable: true,
          maxSelection: 1,
          closeOnSelect: true,
          onSearchChange: (term: string) =>
            handleDeviceSearchChange(term, serviceName),
          onChange: (values: string[]) => {
            const selectedValue = values.length > 0 ? values[0] : "";
            handleDeviceNumberSelection(selectedValue, serviceName);
          },
        });

        // Meter Number Field (Auto-populated)
        fields.push({
          name: `${fieldNamePrefix}_meter_no`,
          label: `${serviceName} Meter No`,
          type: "text",
          disabled: true,
          placeholder: "Auto-populated when device is selected",
          required: false,
          group: `${serviceName} Meter`,
          groupOrder: 2,
        });

        fields.push({
          name: `${fieldNamePrefix}_meter_id`,
          label: `${serviceName} Meter Id`,
          type: "hidden",
          group: `${serviceName} Meter`,
          groupOrder: 2,
        });
      });

      // Service Order Details Section (Static)
      const serviceOrderFields: FormField[] = [
        {
          name: "so_id",
          label: "Service Order",
          type: "multi-select",
          required: true,
          placeholder: searchTerm
            ? `Search results for "${searchTerm}"...`
            : "Search for work orders...",
          options: workOrderOptions,
          group: "Service Order Details",
          groupOrder: 1,
          searchable: true,
          clearable: true,
          maxSelection: 1,
          closeOnSelect: true,
          onSearchChange: handleSearchChange,
          onChange: (values: string[]) => {
            const selectedValue = values.length > 0 ? values[0] : "";
            handleWorkOrderSelection(selectedValue);
          },
          helperText: selectedWorkOrder
            ? `Selected: ${selectedWorkOrder.jobCardNo}`
            : searchTerm && workOrderOptions.length > 0
              ? `${workOrderOptions.length} work orders found`
              : "Type to search for work orders",
        },
        {
          name: "assigned_to",
          label: "Assigned to",
          type: "text",
          disabled: true,
          placeholder: "Auto-populated when work order is selected",
          group: "Service Order Details",
          groupOrder: 2,
        },
        {
          name: "completion_date",
          label: "Completion Date",
          type: "datepicker",
          disabled: true,
          required: true,
          placeholder: "Select completion date",
          group: "Service Order Details",
          groupOrder: 3,
        },
      ];

      return [...fields, ...serviceOrderFields];
    }, [
      availableUtilityServices,
      workOrderOptions,
      selectedWorkOrder,
      deviceSearchTerms,
      meterQueriesFingerprint,
    ]);

    // Update form fields when configuration changes
    useEffect(() => {
      setFormFields(generateFormFields);
    }, [generateFormFields]);

    // Handle work order selection
    const handleWorkOrderSelection = (jobCardNo: string) => {
      const selectedOrder = workorderDetail?.find(
        (order: any) => order.id === jobCardNo,
      );

      if (selectedOrder) {
        setSelectedWorkOrder(selectedOrder);

        setTimeout(() => {
          form.setValue("assigned_to", selectedOrder.fieldForceName);
          form.setValue("completion_date", selectedOrder.completedDate);
        }, 100);
      }
    };

    // Handle form submission
    const handleSubmit = async (data: any) => {
      console.log("Form submitted with data:", data);
      console.log("Selected work order:", selectedWorkOrder);
      console.log("Selected device numbers:", selectedDeviceNumbers);

      if (onSubmit) {
        await onSubmit({
          ...data,
          selected_work_order: selectedWorkOrder,
          selected_device_numbers: selectedDeviceNumbers,
        });
      }
    };

    // Handle form reset
    const handleReset = () => {
      form.reset();
      setSelectedWorkOrder(null);
      setSearchTerm("");
      setDeviceSearchTerms({});
      setSelectedDeviceNumbers({});
      console.log("Form reset");
    };

    // Handle field change
    const handleFieldChange = (fieldName: string, value: any) => {
      console.log(`Field ${fieldName} changed to:`, value);

      if (fieldName.includes("_device_no")) {
        const servicePrefix = fieldName.replace("_device_no", "");
        const serviceName =
          servicePrefix.charAt(0).toUpperCase() + servicePrefix.slice(1);

        const selectedValue = Array.isArray(value)
          ? value.length > 0
            ? value[0]
            : ""
          : value;
        handleDeviceNumberSelection(selectedValue, serviceName);
      }

      if (fieldName === "so_id") {
        const selectedValue = Array.isArray(value)
          ? value.length > 0
            ? value[0]
            : ""
          : value;
        handleWorkOrderSelection(selectedValue);
      }
    };

    if (formFields.length === 0) {
      return (
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading form fields...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-6xl mx-auto bg-white">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={handleReset}
          loading={loading}
          onFieldChange={handleFieldChange}
          config={{
            layout: "vertical",
            spacing: "medium",
            showSubmitButton: false,
            showResetButton: false,
            submitButtonText: "Submit Meter Removal",
            resetButtonText: "Reset Form",
            showProgress: false,
            classes: {
              form: "space-y-6",
              fieldGroup:
                "mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200",
              fieldContainer: "mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              input:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
              select:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
              error: "text-red-500 text-sm mt-1",
              helperText: "text-gray-500 text-sm mt-1",
              required: "text-red-500",
              submitButton:
                "px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2",
              resetButton:
                "px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50",
              buttonContainer: "flex gap-4 pt-6 border-t border-gray-200",
            },
          }}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
            lg: 2,
            xl: 2,
          }}
        />
      </div>
    );
  },
);

MeterRemovalFormWithService.displayName = "MeterRemovalFormWithService";

export default MeterRemovalFormWithService;
